import { v4 as uuidv4 } from 'uuid';
import omit from 'lodash/omit';
import { HandleType } from '@/enums/handle-type.enum';
import { getHandleId, getNodeTemplates } from '@/components/Nodes/Utils';
import { NodeDefinition } from '@/types/api/nodeDefinition';
import { removeAnthropicModelsIfNeeded } from '@/utils/ModelProviders/anthropic.utils';
import { UndoRedoEntry } from '../../undoRedo';
import { createNewNode } from '../../useGraph';
import { type FlowGraph } from '../../FlowGraph';
import { getParameterTranslation } from './parameters.utils';
import type { NodeId, ParameterValue } from 'web';
import type { AiModelData } from '@/types/nodes/model';
import type { NodeDataWithParams, ParameterInfo, Schema } from '@/types/node';

export class ParameterView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  getParameterIds(): string[] {
    return this.graph.wasm.call((wasm) => wasm.parameterIds(this.nodeId)) ?? [];
  }

  getParameterInfo(key: string): ParameterInfo | undefined {
    const { title, description } = this.getParameterTitleAndDescription(key);
    const constraint = this.graph.wasm.call((wasm) => wasm.parameterConstraint(this.nodeId, key)) ?? {
      type: 'string',
    };
    return {
      ...constraint,
      title,
      description,
    };
  }

  isParameterExposed(key: string): boolean {
    return this.graph.wasm.call((wasm) => wasm.isParameterExposed(this.nodeId, key)) ?? false;
  }

  getParameterValue(key: string): ParameterValue | undefined {
    return this.graph.wasm.call((wasm) => wasm.evaluateParameter(this.nodeId, key));
  }

  setParameterValue(key: string, newValue: ParameterValue, ongoing: boolean = false) {
    this.graph.edit(() => this.setParameterValueInternal(key, newValue), ongoing);
    this.graph.updateNodeOutputs(this.nodeId);
  }

  private setParameterValueInternal(key: string, newValue: ParameterValue): UndoRedoEntry {
    const undoEntry = this.graph.store.updateNodeData<NodeDataWithParams>(this.nodeId, (prevData) => ({
      params: {
        ...prevData.params,
        [key]: convertParameterValueToLegacyValue(newValue),
      },
    }));
    undoEntry.add(this.graph.wasmChange((wasm) => wasm.setParameterValue(this.nodeId, key, newValue)));
    return undoEntry;
  }

  exposeParameter(key: string, nodeTypes: NodeDefinition[]) {
    this.graph.edit(() => {
      const { params, schema } = this.graph.store.getNodeData<NodeDataWithParams>(this.nodeId);

      const sanitizedSchema = { ...schema };
      if (this.isAnyLlmLikeModel() && sanitizedSchema.model) {
        sanitizedSchema.model.options = removeAnthropicModelsIfNeeded(sanitizedSchema.model.options ?? []);
      }

      const undoEntry = UndoRedoEntry.empty();

      if (typeof sanitizedSchema[key] !== 'object') throw new Error(`${key} not present in sanitizedSchema`);
      const propertySchema = sanitizedSchema[key];

      // 1. create and add handle
      undoEntry.add(
        this.graph.addNodeInputHandle(this.nodeId, key, {
          required: propertySchema.required,
          description: propertySchema.description,
          format: propertySchema.type || 'text',
          order: propertySchema.order,
          type: getHandleType(propertySchema.type),
          id: uuidv4(),
        }),
      );

      // 2. create new node with params as values, connect
      const newNodesData = getNodeTemplates(propertySchema.type, params[key], propertySchema);
      if (newNodesData) {
        const originNode = this.graph.store.getNode(this.nodeId);
        const position = {
          x: originNode.position.x - 550,
          y: originNode.position.y + 50,
        };

        const newNode = createNewNode(newNodesData.template.id, nodeTypes, position, newNodesData.template.initialData);
        if (newNode) {
          if (newNode.type === 'muxv2') {
            const newNodeData = newNode.data as NodeDataWithParams;
            newNodeData.schema['options'].exposed = false;
          }
          const sourceHandleId = getHandleId(newNode.id, 'output', newNodesData.handleName);
          const targetHandleId = getHandleId(this.nodeId, 'input', key);

          undoEntry.add(this.graph.addNodes([newNode]));
          this.graph.updateNodeOutputs(newNode.id, false);
          const newEdge = this.graph.createEdge(newNode.id, this.nodeId, sourceHandleId, targetHandleId);
          undoEntry.add(this.graph.addEdges([newEdge]));
        }
      } else {
        undoEntry.add(this.graph.wasmChange((wasm) => wasm.setNodeInput(this.nodeId, key, null)));
      }

      // 3. disable the property in the dynamic fields
      undoEntry.add(setSchemaFieldExposed(this.graph, this.nodeId, key, true));

      return undoEntry;
    });
  }

  collapseParameter(key: string) {
    this.graph.edit(() => {
      const undoEntry = UndoRedoEntry.empty();

      const value = this.graph.wasm.call((wasm) => wasm.evaluateValidParameter(this.nodeId, key));

      // 1. remove connected node if it still exists and has no other connections
      const targetHandleId = getHandleId(this.nodeId, 'input', key);
      const edges = this.graph.store.getEdges();
      const edgeToRemove = edges.find((e) => e.targetHandle === targetHandleId);
      if (edgeToRemove) {
        const nodeToRemove = edgeToRemove.source;
        const nodeHasOtherEdges = edges.some(
          (e) => e.id !== edgeToRemove.id && (e.source === nodeToRemove || e.target === nodeToRemove),
        );
        if (nodeHasOtherEdges) {
          undoEntry.add(this.graph.removeEdges([edgeToRemove.id]));
        } else {
          undoEntry.add(this.graph.removeNodes([nodeToRemove]));
        }
      }

      // 2. remove handle for the property
      undoEntry.add(removeNodeInputHandle(this.graph, this.nodeId, key));

      // 3. enable property in dynamic fields
      undoEntry.add(setSchemaFieldExposed(this.graph, this.nodeId, key, false));

      // 4. set parameter value
      if (value !== undefined) {
        undoEntry.add(this.setParameterValueInternal(key, value));
      }

      return undoEntry;
    });
    this.graph.updateNodeOutputs(this.nodeId);
  }

  /**
   * any_llm, prompt_enhancer, image_describer all use any_llm model "type"
   */
  public isAnyLlmLikeModel(): boolean {
    const nodeType = this.graph.getNodeType(this.nodeId);
    const nodeData = this.graph.store.getNodeData<AiModelData>(this.nodeId);
    if (nodeType === 'custommodelV2') {
      const type = nodeData.kind.type;
      return type === 'any_llm' || type === 'prompt_enhancer' || type === 'image_describer';
    }
    return false;
  }

  private getParameterTitleAndDescription(parameterKey: string): { title: string; description: string } {
    const nodeType = this.graph.getNodeType(this.nodeId);

    switch (nodeType) {
      case 'custommodelV2': {
        const modelKind = this.graph.store.getNodeData<AiModelData>(this.nodeId).kind;
        if (modelKind.type === 'wildcard') {
          // If wildcard, use the title and description from the data. Note there's no translation for the user imported models!
          const parameter = modelKind.parameters.find(([schema, _]) => schema.id === parameterKey)?.[0];
          return {
            title: parameter?.title ?? '',
            description: parameter?.description ?? '',
          };
        }
        return getParameterTranslation(nodeType, parameterKey, modelKind.type);
      }
      case 'muxv2':
        return getParameterTranslation(nodeType, parameterKey);
      case 'media_iterator':
        // media iterator doesn't have any parameters to display
        // we are returning a default title and description to prevent errors (https://linear.app/weavy/issue/WEA-2453)
        return {
          title: '',
          description: '',
        };
      case 'masks':
        return getParameterTranslation(nodeType, parameterKey);
    }
    throw new Error(`Invalid parameter ${parameterKey} for node type ${nodeType}`);
  }
}

export function convertParameterValueToLegacyValue(newValue: ParameterValue): any {
  let legacyValue: any = newValue.value;
  if (newValue.type === 'image_size') {
    if (newValue.value.type === 'custom') {
      legacyValue = { width: newValue.value.width, height: newValue.value.height };
    } else if (newValue.value.value === 'match_input') {
      legacyValue = null;
    } else {
      legacyValue = newValue.value.value;
    }
  }
  return legacyValue;
}

function getHandleType(type: Schema['type'] | undefined): HandleType {
  switch (type) {
    case 'integer':
    case 'input':
    case 'input-integer':
    case 'input-float':
    case 'input-number':
    case 'number':
      return HandleType.Number;
    case 'string':
    case 'text':
      return HandleType.Text;
    case 'seed':
      return HandleType.Seed;
    case 'array':
      return HandleType.Array;
    case 'enum':
      return HandleType.Text;
    case 'boolean':
      return HandleType.Boolean;
    default: {
      return HandleType.Any;
    }
  }
}

function setSchemaFieldExposed(flowGraph: FlowGraph, nodeId: NodeId, key: string, exposed: boolean): UndoRedoEntry {
  const { schema } = flowGraph.store.getNodeData<NodeDataWithParams>(nodeId);
  return flowGraph.store.updateNodeData<NodeDataWithParams>(nodeId, () => ({
    schema: {
      ...schema,
      [key]: {
        ...schema[key],
        exposed,
      },
    },
  }));
}

function removeNodeInputHandle(flowGraph: FlowGraph, nodeId: NodeId, key: string) {
  return flowGraph.store.updateNodeData(nodeId, (nodeData) => ({
    handles: {
      ...nodeData.handles,
      input: omit(nodeData.handles.input, key),
    },
  }));
}
